/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import Box from '@mui/material/Box';
import React from 'react';
import { LineSegment, VictoryAxis, VictoryBar, VictoryChart, VictoryLabel, VictoryVoronoiContainer } from 'victory';
import appTheme from '../../common/theme/appTheme.json';
import { CustomTooltip, TOOLTIP_WIDTH } from './UsageLineChart';
import { AggregationInterval } from '../../../shared/entities/dataUsage';

export interface DataPoint {
  x: Date; // Date
  y: number; // Value
}

export interface Series {
  data: DataPoint[];
  name: string;
  tooltipShortenedName: string;
}

interface UsageBarChartProps {
  series: Series[];

  placeholder?: React.ReactNode;
  showPlaceholder?: boolean;

  aggregationInterval?: AggregationInterval | null;
  userEndDateMs?: number;

  width: number;
}

export const BAR_CHART_WIDTH = 32;
const BAR_CHART_CONTAINER_HEIGHT = 343;

const UsageBarChart = ({
  series,
  placeholder,
  showPlaceholder,
  aggregationInterval,
  userEndDateMs,
  width,
}: UsageBarChartProps) => {
  const baseLabelStyles = {
    fontFamily: appTheme.appFontFamily,
    fontSize: 12,
    lineHeight: 16,
    padding: 10,
    fill: appTheme.mptFormLabelText,
    stroke: 'transparent',
  };
  const theme = {
    axis: {
      style: {
        axis: {
          stroke: appTheme.secondaryBackgroundColor,
          strokeWidth: 2,
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
        },
        axisLabel: { textAnchor: 'middle', padding: 25 },
        grid: {
          fill: 'none',
          stroke: 'none',
          pointerEvents: 'painted',
        },
        ticks: {
          fill: 'transparent',
          size: 1,
          stroke: 'transparent',
        },
        tickLabels: baseLabelStyles,
      },
    },
  };

  return (
    <Box
      sx={{
        width: `${width}px`,
        height: `${BAR_CHART_CONTAINER_HEIGHT}px`,
        position: 'relative',
      }}
    >
      <Box
        sx={{
          width: `${width}px`,
          height: `${BAR_CHART_CONTAINER_HEIGHT}px`,
          position: 'relative',
        }}
      >
        <VictoryChart
          theme={theme}
          scale={{ x: 'time' }}
          domain={{ y: [0, 100] }}
          domainPadding={{ y: [40, 40], x: [0, TOOLTIP_WIDTH / 2] }}
          width={width}
          height={BAR_CHART_CONTAINER_HEIGHT}
          padding={{ top: 0, left: 75, bottom: 25, right: 0 }}
          containerComponent={
            <VictoryVoronoiContainer
              voronoiDimension="x"
              mouseFollowTooltips={true}
              activateLabels={true}
              labels={(datum: any) => {
                // This will not be used, but need to include the "labels" prop to make the labelComponent to work
                return 'placeholder';
              }}
              labelComponent={
                <CustomTooltip
                  tooltipNames={series.map(data => data.tooltipShortenedName)}
                  serviceAvailability={true}
                  aggregationInterval={aggregationInterval}
                  userEndDateMs={userEndDateMs}
                />
              }
            />
          }
        >
          <VictoryAxis
            fixLabelOverlap
            tickComponent={<LineSegment y1={0} y2={BAR_CHART_CONTAINER_HEIGHT - 25} />}
            tickLabelComponent={showPlaceholder ? <VictoryLabel text={''} /> : <VictoryLabel />}
            style={{
              ticks: { stroke: appTheme.secondaryBackgroundColor },
              tickLabels: {
                fill: appTheme.primaryTextColor,
              },
              axis: { stroke: appTheme.secondaryBackgroundColor },
            }}
          />
          {series.map((seriesData, index) => (
            <VictoryBar
              key={seriesData.name}
              name={seriesData.name}
              barWidth={seriesData.data.length < 20 ? BAR_CHART_WIDTH : undefined}
              style={{ data: { fill: appTheme.mptNominalServiceFlightPathColor } }}
              data={seriesData.data}
              alignment="start"
              padding={{ left: 20 }}
            />
          ))}
          <VictoryAxis
            dependentAxis
            tickComponent={undefined}
            tickFormat={tickValue => {
              return `${tickValue}%`;
            }}
            style={{
              ticks: { stroke: 'transparent' },
              tickLabels: {
                fill: appTheme.primaryTextColor,
              },
              axis: { stroke: appTheme.secondaryBackgroundColor },
            }}
          />
        </VictoryChart>
        {!!showPlaceholder && !!placeholder && placeholder}
      </Box>
    </Box>
  );
};

export default UsageBarChart;
